import { useRef, useCallback } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";

type QueueItem<T, R> = {
  arg: T;
  resolve: (value: MutationResult<R>) => void;
  reject: (reason?: unknown) => void;
};

type MutationResult<T> = {
  data?: T;
  error?: FetchBaseQueryError | SerializedError;
};

type UnwrappedPromise<T> = Promise<MutationResult<T>> & {
  unwrap: () => Promise<T>;
};

export function useQueuedMutation<T extends object, R extends { lastModifiedDate?: string }>(
  mutationFn: (arg: T) => Promise<MutationResult<R>>,
) {
  const isProcessing = useRef(false);
  const queue = useRef<QueueItem<T, R>[]>([]);
  const lastModifiedDateRef = useRef<string | undefined>(undefined);

  const processQueue = useCallback(async () => {
    if (isProcessing.current || queue.current.length === 0) return;

    isProcessing.current = true;
    const { arg, resolve, reject } = queue.current.shift()!;

    // Вставляем lastModifiedDate из предыдущего ответа (если есть)
    const updatedArg = {
      ...arg,
      ...(lastModifiedDateRef.current && { lastModifiedDate: lastModifiedDateRef.current }),
    };

    try {
      const result = await mutationFn(updatedArg as T);

      // Обновляем lastModifiedDate для следующего запроса
      if (result.data?.lastModifiedDate) {
        lastModifiedDateRef.current = result.data.lastModifiedDate;
      }

      resolve(result);
    } catch (err) {
      reject(err);
    } finally {
      isProcessing.current = false;
      if (queue.current.length > 0) {
        processQueue();
      }
    }
  }, [mutationFn]);

  const enqueue = useCallback(
    (arg: T): UnwrappedPromise<R> => {
      const promise = new Promise<MutationResult<R>>((resolve, reject) => {
        queue.current.push({ arg, resolve, reject });
        processQueue();
      }) as UnwrappedPromise<R>;

      promise.unwrap = async () => {
        const result = await promise;
        if (result.error) throw result.error;
        if (!result.data) throw new Error("No data returned from mutation");
        return result.data;
      };

      return promise;
    },
    [processQueue],
  );

  return enqueue;
}
